import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ModalCloseButton from "@/components/base/ModalCloseButton";
import {
  MODAL_FOOTER_CANCEL_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_STYLE,
} from "@/components/base/modalFooterButtonStyles";
import { docTagApi, toErrorMessage, type DocTaxonomyItem } from "@/services/docTaxonomy";
import ConfirmDialog from "../recommendations/components/ConfirmDialog";
import AdminLayout from "@/components/layout/AdminLayout";
import { AdminSearchForm } from "@/components/admin/AdminTableToolbar";

type DocumentTag = DocTaxonomyItem;

interface TagModalProps {
  mode: "add" | "edit";
  tag?: DocumentTag;
  onSave: (name: string, prompt: string) => void;
  onCancel: () => void;
}

function TagModal({ mode, tag, onSave, onCancel }: TagModalProps) {
  const { t } = useTranslation();
  const isBuiltIn = tag?.is_builtin ?? false;
  const isReadOnly = mode === "edit" && tag ? !tag.is_deletable : false;
  const canEditName = !isReadOnly;
  const canEditPrompt = !isReadOnly;

  const [name, setName] = useState(tag?.name ?? "");
  const [prompt, setPrompt] = useState(tag?.prompt ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave(name.trim(), prompt.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-sm w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-50">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {mode === "add" ? t("docTags.modal.newTitle") : t("docTags.modal.editTitle")}
            </h3>
          </div>
          <ModalCloseButton ariaLabel={t("common.close")} onClick={onCancel} />
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              {t("docTags.tagName")} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => canEditName && setName(e.target.value)}
              placeholder={t("docTags.placeholder.name")}
              disabled={!canEditName}
              className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
              autoFocus={canEditName}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              {t("docTags.tagStandard")}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => canEditPrompt && setPrompt(e.target.value)}
              placeholder={t("docTags.placeholder.prompt")}
              rows={4}
              disabled={!canEditPrompt}
              className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 bg-white resize-none transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          {isReadOnly ? (
            <button
              onClick={onCancel}
              className="text-xs px-4 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#830051" }}
            >
              {t("common.close")}
            </button>
          ) : (
            <>
              <button onClick={onCancel} className={MODAL_FOOTER_CANCEL_BUTTON_CLASS}>
                {t("common.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={!name.trim() || saving}
                className={`${MODAL_FOOTER_PRIMARY_BUTTON_CLASS} disabled:opacity-40 disabled:cursor-not-allowed`}
                style={MODAL_FOOTER_PRIMARY_BUTTON_STYLE}
              >
                {saving ? (
                  <span className="flex items-center gap-1">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t("common.saving")}
                  </span>
                ) : (
                  t("common.save")
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DocumentTagsPage() {
  const { t } = useTranslation();
  const [tags, setTags] = useState<DocumentTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Modal states
  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);
  const [editTarget, setEditTarget] = useState<DocumentTag | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<DocumentTag | null>(null);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 后端已排好序（Custom 创建时间倒序在前，System 固定序在后），此处不再二次排序
      setTags(await docTagApi.list());
    } catch (err) {
      setError(toErrorMessage(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleSave = async (name: string, prompt: string) => {
    try {
      if (modalMode === "add") {
        await docTagApi.create(name, prompt || null);
      } else if (modalMode === "edit" && editTarget) {
        // System 标签（含 Personal Doc）整体不可编辑，服务端会直接拒绝，
        // 故此处无需再对 Prompt 做单独的可编辑性判断。
        await docTagApi.update(editTarget.id, name, prompt || null);
      }
    } catch (err) {
      // 重名、System 行保护等服务端校验错误在此展示，弹窗保持打开供用户修改
      setError(toErrorMessage(err, t("common.error")));
      return;
    }
    setModalMode(null);
    setEditTarget(undefined);
    await fetchTags();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await docTagApi.remove(deleteTarget.id);
    } catch (err) {
      setError(toErrorMessage(err, t("common.error")));
      return;
    }
    setDeleteTarget(null);
    await fetchTags();
  };

  const filtered = tags.filter((tag) => {
    const matchSearch =
      !searchQuery.trim() ||
      tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tag.stable_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tag.prompt?.toLowerCase() ?? "").includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-5 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-1.5">
              {t("docTags.title")}
              <span className="relative group cursor-help">
                <i className="ri-question-line text-gray-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                <span className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                  {t("docTags.tooltip")}
                </span>
              </span>
            </h2>
          </div>
          <button
            onClick={() => {
              setEditTarget(undefined);
              setModalMode("add");
            }}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-lg cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 shrink-0 ml-4"
            style={{ backgroundColor: "#830051" }}
          >
            <i className="ri-add-line text-base"></i>
            {t("docTags.newTag")}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 text-sm"></i>
            <span className="text-xs text-red-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-xs text-red-500 hover:text-red-700 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="mb-4 flex items-center gap-2">
          <AdminSearchForm
            value={searchInput}
            onChange={setSearchInput}
            onSearch={() => setSearchQuery(searchInput)}
            searchLabel={t("common.search")}
            placeholder={t("docTags.searchPlaceholder")}
            showReset={searchQuery !== "" || searchInput !== ""}
            onReset={() => {
              setSearchQuery("");
              setSearchInput("");
            }}
            resetLabel={t("common.reset")}
          />
          <div className="flex-1" />
          <span className="text-xs text-gray-400 whitespace-nowrap ml-1">
            {t("docTags.count", { count: filtered.length })}
          </span>
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-[#830051] rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-xs text-gray-400">{t("common.loading")}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-gray-100 rounded-xl overflow-hidden px-5 py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-price-tag-3-line text-lg text-gray-400"></i>
            </div>
            <p className="text-sm text-gray-500">
              {searchQuery ? t("docTags.empty.noMatching") : t("docTags.empty.noTags")}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {searchQuery ? t("docTags.empty.adjustSearch") : t("docTags.empty.clickNew")}
            </p>
          </div>
        ) : (
          <>
            {/* Custom Tags Section */}
            {filtered.some((t) => t.is_editable) && (
              <div className="border border-gray-100 rounded-xl overflow-hidden mb-4">
                {/* Section Header */}
                <div
                  className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100"
                  style={{ backgroundColor: "#fafafa" }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-gray-400 text-base"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {t("docTags.customTags")}
                  </span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "rgba(131,0,81,0.1)", color: "#830051" }}
                  >
                    {filtered.filter((t) => t.is_editable).length}
                  </span>
                </div>
                {/* Rows */}
                <div className="divide-y divide-gray-50 bg-white">
                  {filtered
                    .filter((t) => t.is_editable)
                    .map((tag) => (
                      <div
                        key={tag.id}
                        className="group flex items-start gap-4 px-5 py-3 transition-colors hover:bg-gray-50"
                      >
                        <div className="flex-none w-[180px] pt-0.5">
                          <span className="text-sm font-medium text-gray-800">{tag.name}</span>
                        </div>
                        <div className="flex-1 min-w-[400px] pt-0.5">
                          <p className="text-xs leading-relaxed line-clamp-2">
                            {tag.prompt ? (
                              <span className="text-gray-500">{tag.prompt}</span>
                            ) : (
                              <span className="text-gray-300 italic">
                                {t("docTags.empty.noPromptDefined")}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex-none w-[104px] flex items-center gap-1 sticky right-0 z-50 pt-0.5 bg-inherit group-hover:bg-gray-50 px-1 border-l border-gray-100">
                          <button
                            onClick={() => {
                              setEditTarget(tag);
                              setModalMode("edit");
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                            title={t("common.edit")}
                          >
                            <i className="ri-pencil-line text-sm text-gray-400"></i>
                          </button>
                          {tag.is_deletable && (
                            <button
                              onClick={() => setDeleteTarget(tag)}
                              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 cursor-pointer transition-colors"
                              title={t("common.delete")}
                            >
                              <i className="ri-delete-bin-line text-sm text-red-400"></i>
                            </button>
                          )}
                          {!tag.is_deletable && (
                            <div className="w-7 h-7 flex items-center justify-center">
                              <i
                                className="ri-lock-line text-sm text-gray-300"
                                title={t("docTags.modal.builtInNotice")}
                              ></i>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* System Tags Section */}
            {filtered.some((t) => !t.is_editable) && (
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                {/* Section Header */}
                <div
                  className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100"
                  style={{ backgroundColor: "#fafafa" }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-line text-gray-500 text-base"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {t("docTags.systemTags")}
                  </span>
                  <span className="relative group cursor-help">
                    <i className="ri-question-line text-gray-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                    <span className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                      {t("docTags.systemTagsTooltip")}
                    </span>
                  </span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    {filtered.filter((t) => !t.is_editable).length}
                  </span>
                </div>
                {/* Rows */}
                <div className="divide-y divide-gray-50 bg-white">
                  {filtered
                    .filter((t) => !t.is_editable)
                    .map((tag) => (
                      <div
                        key={tag.id}
                        className="group flex items-start gap-4 px-5 py-3 transition-colors border-l-gray-300 hover:bg-gray-100/60"
                      >
                        <div className="flex-none w-[180px] pt-0.5">
                          <span className="text-sm font-medium text-gray-500">{tag.name}</span>
                        </div>
                        <div className="flex-1 min-w-[400px] pt-0.5">
                          <p className="text-xs leading-relaxed line-clamp-2">
                            {tag.name === "Personal Doc" ? (
                              <span className="text-gray-400">
                                {t("docTags.personalDocDescription")}
                              </span>
                            ) : tag.prompt ? (
                              <span className="text-gray-400">{tag.prompt}</span>
                            ) : (
                              <span className="text-gray-300 italic">
                                {t("docTags.empty.noPromptNeeded")}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex-none w-[104px] flex items-center gap-1 sticky right-0 z-50 pt-0.5 bg-inherit group-hover:bg-gray-100/60 px-1 border-l border-gray-100">
                          <div
                            className="w-7 h-7 flex items-center justify-center rounded-md"
                            title={t("docTags.modal.builtInNotice")}
                          >
                            <i className="ri-lock-line text-sm text-gray-400"></i>
                          </div>
                          {tag.is_deletable && (
                            <button
                              onClick={() => setDeleteTarget(tag)}
                              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 cursor-pointer transition-colors"
                              title={t("common.delete")}
                            >
                              <i className="ri-delete-bin-line text-sm text-red-400"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Tag Modal */}
        {modalMode && (
          <TagModal
            mode={modalMode}
            tag={editTarget}
            onSave={handleSave}
            onCancel={() => {
              setModalMode(null);
              setEditTarget(undefined);
            }}
          />
        )}

        {/* Delete Confirm */}
        {deleteTarget && (
          <ConfirmDialog
            title={t("docTags.modal.deleteTitle")}
            description={t("docTags.modal.deleteDesc", { name: deleteTarget.name })}
            confirmLabel={t("common.delete")}
            confirmDanger
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}
